// Removed reactive import - service is now a utility class

class CurrencyService {
    constructor() {
        // Default values - will be overwritten by init
        this._currency = 'EUR';
        this._rates = { EUR: 1 };
        this._symbols = {
            'EUR': '€',
            'USD': '$',
            'GBP': '£'
        };
    }

    // Initialize with data from Inertia props (code, symbol, and rates)
    init(currencyData) {
        if (currencyData) {
            this._currency = currencyData.code || this._currency;
            this._symbols = { // Overwrite symbols based on initial data + defaults
                 'EUR': '€',
                 'USD': '$',
                 'GBP': '£',
                 ...(currencyData.symbols || {}) // Allow passing symbols if needed
            };
             // Ensure the current currency has a symbol
            this._symbols[this._currency] = currencyData.symbol || this._symbols[this._currency] || this._currency;

            if (currencyData.rates && typeof currencyData.rates === 'object') {
                this._rates = { ...currencyData.rates }; // Store provided rates
                 // Ensure EUR base rate exists if not provided
                 if (!this._rates.hasOwnProperty('EUR')) {
                    this._rates['EUR'] = 1.0; // Assuming EUR is the base
                 }
            } else {
                 this._rates = { EUR: 1 }; // Fallback to default
            }
        }
         // Ensure symbols for all provided rates exist after init
        for (const code in this._rates) {
            if (!this._symbols[code]) {
                this._symbols[code] = code; // Fallback symbol
            }
        }
        return this;
    }

    // --- Getters ---
    get currency() {
        return this._currency; // Returns the initially set currency
    }
    get rates() {
        return this._rates; // Returns the initially set rates
    }
    get symbols() {
        return this._symbols; // Returns the initially set symbols
    }
    // --- End Getters ---

    // Accepts target currency code and optional current rates
    formatPrice(amount, targetCurrencyCode, includeSymbol = true, currentRates = null) {
        const numericAmount = parseFloat(amount);
        if (isNaN(numericAmount)) return 'N/A';

        // Pass currentRates to convert method
        const convertedAmount = this.convert(numericAmount, targetCurrencyCode, currentRates);
        if (isNaN(convertedAmount)) return 'N/A';

        // Use stored symbols, assuming they don't change dynamically as often as rates
        const symbol = this._symbols[targetCurrencyCode] || targetCurrencyCode;
        return includeSymbol ? symbol + convertedAmount.toFixed(2) : convertedAmount.toFixed(2);
    }

    // Accepts target currency code and optional current rates
    convert(amount, targetCurrencyCode, currentRates = null) {
        const numericAmount = parseFloat(amount);
        if (isNaN(numericAmount)) return NaN;

        // Use provided currentRates if available, otherwise fallback to initial rates
        const ratesToUse = currentRates && typeof currentRates === 'object' && Object.keys(currentRates).length > 0 ? currentRates : this._rates;

        const rate = ratesToUse[targetCurrencyCode];
        if (typeof rate !== 'number' || isNaN(rate)) {
             console.warn(`Currency rate for ${targetCurrencyCode} is missing or invalid in rates used:`, ratesToUse);
             // Attempt conversion using EUR as base if target is not EUR and EUR rate exists
             if (targetCurrencyCode !== 'EUR' && ratesToUse['EUR'] === 1) {
                 console.warn(`Attempting conversion assuming input amount is EUR.`);
                 return numericAmount; // Assume amount is already EUR if rate is missing
             }
             return NaN; // Cannot convert
        }
        // Assumes amounts passed are always in EUR (base currency)
        // If amounts can be in other currencies, need 'fromCurrencyCode' argument too
        return targetCurrencyCode === 'EUR' ? numericAmount : numericAmount * rate;
    }

     // Accepts source currency code
     // Converts an amount FROM sourceCurrencyCode TO EUR
    convertToEur(amount, sourceCurrencyCode) {
        const numericAmount = parseFloat(amount);
        if (isNaN(numericAmount)) return NaN;
        if (sourceCurrencyCode === 'EUR') return numericAmount; // Already in EUR

        const rate = this._rates[sourceCurrencyCode];
        if (typeof rate !== 'number' || isNaN(rate) || rate === 0) {
             console.warn(`Cannot convert to EUR: Currency rate for ${sourceCurrencyCode} is missing, invalid, or zero.`);
             return NaN;
        }
        return numericAmount / rate;
    }

    // Accepts target currency code and optional current rates
    formatMoney(amount, targetCurrencyCode, currentRates = null) {
        // Ensure targetCurrencyCode is provided, fallback to initial currency if needed
        const codeToUse = targetCurrencyCode || this._currency;
        // Pass currentRates to formatPrice
        return this.formatPrice(amount, codeToUse, true, currentRates);
    }
}

// Create a singleton instance
const currencyService = new CurrencyService();

// Make the service instance available for import
export default currencyService;
